import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, ExternalLink, Search } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLinks = () => {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [newName, setNewName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem('nutrinori_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const token = localStorage.getItem('nutrinori_token');
      const response = await axios.get(`${API}/links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinks(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('nutrinori_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const token = localStorage.getItem('nutrinori_token');
      await axios.post(
        `${API}/links`,
        { slug: newSlug, name: newName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNewSlug("");
      setNewName("");
      setShowCreate(false);
      fetchLinks();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleDeleteLink = async (slug) => {
    if (!window.confirm('Supprimer le lien "' + slug + '" ?')) return;

    try {
      const token = localStorage.getItem('nutrinori_token');
      await axios.delete(API + '/links/' + slug, {
        headers: { Authorization: 'Bearer ' + token }
      });
      fetchLinks();
    } catch (error) {
      alert("Erreur lors de la suppression");
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('nutrinori_token');
      const response = await axios.get(`${API}/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'nutrinori_clicks.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert("Erreur lors de l'export");
    }
  };

  const filteredLinks = links.filter(link => 
    link.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
    link.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#3D6B4A' }}></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#0A120D' }} data-testid="links-title">
              Liens Influenceurs
            </h1>
            <p className="mt-2" style={{ color: '#6B756D' }}>Gérez vos liens personnalisés</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExportCSV}
              className="btn-secondary"
              data-testid="export-csv-button"
            >
              Export CSV
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="create-link-button"
            >
              <Plus className="w-4 h-4" />
              Nouveau Lien
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#6B756D' }} />
            <input
              type="text"
              placeholder="Rechercher un lien..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
              style={{
                borderColor: 'rgba(61, 107, 74, 0.2)',
                backgroundColor: 'white'
              }}
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Create Link Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="card max-w-md w-full">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#0A120D' }}>
                Créer un Nouveau Lien
              </h2>
              <form onSubmit={handleCreateLink}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#3A453F' }}>
                    Slug (identifiant unique)
                  </label>
                  <input
                    type="text"
                    value={newSlug}
                    onChange={(e) => setNewSlug(e.target.value)}
                    placeholder="nolway"
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      borderColor: 'rgba(61, 107, 74, 0.2)',
                      backgroundColor: 'white'
                    }}
                    required
                    data-testid="slug-input"
                  />
                  <p className="text-xs mt-1" style={{ color: '#6B756D' }}>
                    Lettres, chiffres, tirets et underscores uniquement
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#3A453F' }}>
                    Nom de l'influenceur
                  </label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Nolway"
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      borderColor: 'rgba(61, 107, 74, 0.2)',
                      backgroundColor: 'white'
                    }}
                    required
                    data-testid="name-input"
                  />
                </div>

                {error && (
                  <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                    {error}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreate(false);
                      setError("");
                      setNewSlug("");
                      setNewName("");
                    }}
                    className="btn-secondary flex-1"
                    data-testid="cancel-button"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1"
                    data-testid="submit-link-button"
                  >
                    Créer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Links Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLinks.map((link) => (
            <div key={link.slug} className="card hover:shadow-lg transition-all duration-150">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1" style={{ color: '#0A120D' }}>
                    {link.name}
                  </h3>
                  <p className="text-sm font-mono" style={{ color: '#6B756D' }}>/{link.slug}</p>
                </div>
                <button
                  onClick={() => handleDeleteLink(link.slug)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid={`delete-link-${link.slug}`}
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>

              <div className="mb-4">
                <span className="text-2xl font-bold" style={{ color: '#3D6B4A' }}>
                  {link.clicks_count.toLocaleString()}
                </span>
                <span className="text-sm ml-2" style={{ color: '#6B756D' }}>clics</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/admin/links/${link.slug}`)}
                  className="btn-secondary flex-1 text-sm"
                  data-testid={`view-stats-${link.slug}`}
                >
                  Voir Stats
                </button>
                <button
                  onClick={() => window.open(`${window.location.origin}/${link.slug}`, '_blank')}
                  className="p-2 border rounded-lg hover:bg-gray-50 transition-colors"
                  style={{ borderColor: 'rgba(61, 107, 74, 0.2)' }}
                  data-testid={`open-link-${link.slug}`}
                >
                  <ExternalLink className="w-4 h-4" style={{ color: '#3D6B4A' }} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredLinks.length === 0 && (
          <div className="text-center py-12">
            <p className="text-lg" style={{ color: '#6B756D' }}>
              {searchTerm ? "Aucun lien trouvé" : "Aucun lien créé"}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLinks;
